import {
  Stack,
  Group,
  InputWrapper,
  TextInput,
  Select,
  MultiSelect,
  Switch,
} from "@mantine/core"
import { MonthPickerInput } from "@mantine/dates"
import { UseFormReturnType } from "@mantine/form"
import { useMemo } from "react"

import { BudgetsResponses } from "@costory/types/endpoints/budgets"
import { MetricsResponses } from "@costory/types/endpoints/metrics"
import { CurrencyOptions } from "@costory/types/endpoints/shared"
import { CostType } from "@costory/types/prisma-client"

import dayjs from "@costory/shared/dayjs"

import MetricSelector from "@costory/front/components/dropdown/MetricSelector"
import { BudgetFormValues } from "@costory/front/pages/Budgets/components/BudgetTable"
import { BudgetTableMode } from "@costory/front/pages/Budgets/consts"

export const BudgetToolbar = ({
  form,
  availableVersionSelectItems,
  selectedVersionsIds,
  setSelectedVersionsIds,
  initOptionsCanBeEdited,
  mode,
  availableVirtualDimensions,
  availableTechnicalMetrics,
}: {
  form: UseFormReturnType<BudgetFormValues>
  availableVersionSelectItems: { value: string; label: string }[]
  selectedVersionsIds: string[]
  setSelectedVersionsIds: (value: string[]) => void
  initOptionsCanBeEdited: boolean
  mode: BudgetTableMode
  availableVirtualDimensions: BudgetsResponses.BudgetFormData["availableVirtualDimensions"]
  availableTechnicalMetrics: BudgetsResponses.BudgetFormData["availableTechnicalMetrics"]
}) => {
  const isCreateMode = mode === "create"

  const selectedVirtualDimension = availableVirtualDimensions.find(
    (vd) => vd.id === form.getValues().virtualDimensionId,
  )

  const virtualDimensionsSelectItems = useMemo(
    () =>
      availableVirtualDimensions.map((vd) => ({
        value: vd.id,
        label: vd.name,
      })),
    [availableVirtualDimensions],
  )

  const availableTechnicalMetricsSelectItems = useMemo(
    () =>
      availableTechnicalMetrics.map((bm) => ({
        value: bm.id,
        label: bm.name,
        technical: true,
        name: bm.name,
        id: bm.id,
        group: MetricsResponses.CostCategory.Cost,
        onlyUSD: false,
      })),
    [availableTechnicalMetrics],
  )

  const excludedValuesSelectItems = useMemo(() => {
    return (
      selectedVirtualDimension?.values.map((value) => ({
        value,
        label: value,
      })) ?? []
    )
  }, [selectedVirtualDimension])

  return (
    <Stack>
      <Group justify="space-between" align="start" wrap="nowrap">
        <Group wrap="nowrap">
          <InputWrapper label="Budget name" required>
            <TextInput
              disabled={mode === "view"}
              {...form.getInputProps("name")}
              maw="30vw"
              data-intercom-target="costory-tour-budget-name-input"
            />
          </InputWrapper>
          <InputWrapper label="Version name">
            <TextInput
              disabled={mode === "view"}
              {...form.getInputProps("versionName")}
              maw="30vw"
              data-intercom-target="costory-tour-budget-version-name-input"
            />
          </InputWrapper>
          <InputWrapper label="Currency" required>
            <Select
              disabled={!isCreateMode}
              data={CurrencyOptions}
              placeholder="Select Currency"
              {...form.getInputProps("currency")}
              data-intercom-target="costory-tour-budget-currency-select"
            />
          </InputWrapper>
          <InputWrapper label="Monitor budget">
            <Switch
              disabled={mode === "view"}
              {...form.getInputProps("monitorBudget", { type: "checkbox" })}
              label="Monitor budget"
            />
          </InputWrapper>
        </Group>
      </Group>
      <Group justify="space-between" align="start" wrap="nowrap">
        <Group wrap="nowrap">
          <InputWrapper label="Virtual dimension" required>
            <Select
              maw="15vw"
              disabled={!initOptionsCanBeEdited}
              data={virtualDimensionsSelectItems}
              placeholder="Select virtual dimension"
              {...form.getInputProps("virtualDimensionId")}
              data-intercom-target="costory-tour-budget-virtual-dimension-select"
            />
          </InputWrapper>

          <InputWrapper data-intercom-target="costory-tour-budget-cost-type-select">
            <MetricSelector
              metric={form.getInputProps("costType").value}
              setMetric={(value) =>
                form.setFieldValue("costType", value as CostType)
              }
              metricsOptions={availableTechnicalMetricsSelectItems}
              onlyCostGroup={true}
              label="Cost type"
              setCurrency={() => {}}
            />
          </InputWrapper>

          <InputWrapper label="Excluded values">
            <MultiSelect
              maw="15vw"
              styles={{
                pillsList: {
                  flexWrap: "nowrap",
                  overflow: "hidden",
                },
              }}
              data={excludedValuesSelectItems}
              disabled={!initOptionsCanBeEdited}
              {...form.getInputProps("excludedValues")}
              placeholder="Select values"
              data-intercom-target="costory-tour-budget-excluded-values-select"
            />
          </InputWrapper>

          {mode !== "create" && (
            <InputWrapper label="Compare to older versions">
              <MultiSelect
                maw="15vw"
                disabled={availableVersionSelectItems.length === 0}
                data={availableVersionSelectItems}
                value={selectedVersionsIds}
                onChange={setSelectedVersionsIds}
                placeholder="Select versions"
                data-intercom-target="costory-tour-budget-compare-versions-select"
              />
            </InputWrapper>
          )}
        </Group>
        <Group wrap="nowrap">
          <InputWrapper label="First month" required>
            <MonthPickerInput
              disabled={!isCreateMode}
              value={form.getValues().startDate}
              onChange={(value) => {
                if (!value) {
                  return
                }
                form.setFieldValue("startDate", value)
              }}
              placeholder="Select first month"
              data-intercom-target="costory-tour-budget-start-month-select"
            />
          </InputWrapper>
          <InputWrapper label="Last month" required>
            <MonthPickerInput
              disabled
              value={dayjs(form.getValues().startDate)
                .add(11, "month")
                .toDate()}
            />
          </InputWrapper>
        </Group>
      </Group>
    </Stack>
  )
}
